import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArtworkUpload } from "../components/ArtworkUpload";
import { CheckList, Row } from "./PullUpBuilder";
import { SIGN_PRODUCTS, COASTER_SHAPES, COASTER_QTYS, COASTER_SIZE, coasterPrice, formatAUDexact } from "../data/signsPricing";

export default function CoasterBuilder() {
  const info = SIGN_PRODUCTS.coaster;
  const [shape, setShape] = useState(null);
  const [sides, setSides] = useState("single");
  const [qty, setQty] = useState(COASTER_QTYS[0]);
  const [file, setFile] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const shapeInfo = COASTER_SHAPES.find((s) => s.id === shape);
  const subtotal = coasterPrice(qty, sides);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  const addToCart = () => {
    if (!shapeInfo) { toast.error("Please choose a coaster shape"); return; }
    if (!file) { toast.error("Please upload your artwork first"); return; }
    addItem({
      product_type: "coaster",
      product_name: info.name,
      shape: shapeInfo.label,
      size: COASTER_SIZE,
      quantity: qty,
      unit_price: subtotal / qty,
      line_total: subtotal,
      print_sides: sides,
      artwork_file_id: file.id,
      artwork_filename: file.name,
    });
    toast.success(`${qty.toLocaleString()} × ${shapeInfo.label} coasters added to cart`);
    navigate("/cart");
  };

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6">
        <div className="eyebrow" data-testid="sign-eyebrow">{info.shortName} · Custom print</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight">{info.headline}</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl">{info.tagline}</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card overflow-hidden">
            <div className="aspect-[5/4] relative bg-[#F6F8FD]">
              <img src={info.image} alt={info.imageAlt} className="w-full h-full object-cover" data-testid="sign-product-photo" />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">{info.imageTag}</div>
            </div>
          </div>
          <CheckList title="Ideal for" items={info.uses} grid />
          <CheckList title="About this product" items={info.features} />
        </div>

        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card p-6 space-y-6">
            <ShapePicker shape={shape} setShape={setShape} />
            <div>
              <div className="eyebrow mb-3">Step 2 — Printing</div>
              <div className="flex flex-wrap gap-2">
                {[["single", "Single-sided"], ["double", "Double-sided"]].map(([v, l]) => (
                  <button key={v} onClick={() => setSides(v)} className={`chip ${sides === v ? "chip-active" : ""}`} data-testid={`coaster-sides-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="eyebrow mb-3">Step 3 — Quantity</div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="coaster-qty-options">
                {COASTER_QTYS.map((q) => (
                  <button key={q} onClick={() => setQty(q)} className={`chip justify-center ${qty === q ? "chip-active" : ""}`} data-testid={`coaster-qty-${q}`}>{q.toLocaleString()}</button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Minimum order 500 coasters. Prices are for the whole order.</p>
            </div>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow mb-3">Step 4 — Upload artwork</div>
            <ArtworkUpload file={file} setFile={setFile} />
            <p className="text-xs text-slate-500 mt-3" data-testid="coaster-artwork-guidance">Please supply print-ready artwork at 95 × 95 mm with 3 mm bleed, at 300 dpi. For double-sided, include both sides in one PDF.</p>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow">Live quote</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-5xl font-black price-tick" data-testid="sign-total-exgst">{formatAUDexact(subtotal)}</div>
              <div className="text-sm text-slate-500 font-semibold">ex GST</div>
            </div>
            <div className="text-slate-600 mt-1 text-sm" data-testid="sign-unit-price">{qty.toLocaleString()} coasters · {sides === "double" ? "double-sided" : "single-sided"}</div>
            <div className="mt-5 space-y-2 text-sm">
              <Row label="Product" value={info.name} testid="sign-summary-product"/>
              <Row label="Shape" value={shapeInfo ? shapeInfo.label : "Choose a shape above"} testid="sign-summary-shape"/>
              <Row label="Printing" value={sides === "double" ? "Double-sided" : "Single-sided"} testid="sign-summary-sides"/>
              <Row label="Quantity" value={qty.toLocaleString()} testid="sign-summary-qty"/>
              <Row label="Artwork" value={file ? file.name : "Not uploaded"} testid="sign-summary-artwork"/>
            </div>
            <div className="h-px bg-[#E2DDD5] my-4"/>
            <div className="space-y-1 text-sm">
              <Row label="Subtotal (ex GST)" value={formatAUDexact(subtotal)} testid="sign-row-subtotal"/>
              <Row label="GST (10%)" value={formatAUDexact(gst)} testid="sign-row-gst"/>
              <div className="flex justify-between items-baseline pt-2">
                <div className="font-display font-bold">Total inc GST</div>
                <div className="font-display font-black text-2xl price-tick" data-testid="sign-total-incgst">{formatAUDexact(total)}</div>
              </div>
            </div>
            <button onClick={addToCart} className="btn-primary w-full mt-5" data-testid="sign-add-to-cart">Add to Cart</button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function ShapePicker({ shape, setShape }) {
  return (
    <div>
      <div className="eyebrow mb-3">Step 1 — Shape</div>
      <div className="grid grid-cols-2 gap-3" data-testid="coaster-shape-options">
        {COASTER_SHAPES.map((s) => {
          const active = shape === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setShape(s.id)}
              className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-3 text-center transition-colors ${active ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5] bg-white hover:border-[color:var(--brand)]"}`}
              data-testid={`coaster-shape-${s.id}`}
              aria-pressed={active}
            >
              <div className={`h-20 w-20 border-[3px] border-[#003DA5] bg-[#E8EEF9] ${s.id === "round" ? "rounded-full" : "rounded-[14px]"}`} />
              <div>
                <div className="font-display font-bold">{s.label}</div>
                <div className="text-xs text-slate-500">{s.note}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
